import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { favoriteApi } from '../services/favoriteApi';
import { isNativeMobileApp } from '../utils/mobileApp';

interface FavoritesContextType {
  favorites: Set<number>;
  isLoading: boolean;
  toggleFavorite: (propertyId: number) => Promise<boolean>;
  isFavorited: (propertyId: number) => boolean;
  loadFavorites: () => Promise<void>;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Load favorites from backend
  const loadFavorites = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await favoriteApi.getFavorites();
      
      if (response.success && response.data) {
        const favoriteIds = new Set(response.data.map(fav => fav.property_id));
        setFavorites(favoriteIds);
        console.log(`✅ Loaded ${favoriteIds.size} favorites`);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // APK không dùng danh sách yêu thích — bỏ request lúc mở app.
  useEffect(() => {
    if (isNativeMobileApp()) return;
    loadFavorites();
  }, [loadFavorites]);

  // Toggle favorite status
  const toggleFavorite = useCallback(async (propertyId: number): Promise<boolean> => {
    const wasFavorited = favorites.has(propertyId);
    
    // Optimistic update
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (wasFavorited) {
        newFavorites.delete(propertyId);
      } else {
        newFavorites.add(propertyId);
      }
      return newFavorites;
    });

    try {
      let response;
      
      if (wasFavorited) {
        response = await favoriteApi.removeFavorite(propertyId);
      } else {
        response = await favoriteApi.addFavorite(propertyId);
      }

      if (!response.success) {
        // Revert on error
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          if (wasFavorited) {
            newFavorites.add(propertyId);
          } else {
            newFavorites.delete(propertyId);
          }
          return newFavorites;
        });
        return false;
      }

      return true;
    } catch (error) {
      // Revert on error
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (wasFavorited) {
          newFavorites.add(propertyId);
        } else {
          newFavorites.delete(propertyId);
        }
        return newFavorites;
      });
      console.error('Error toggling favorite:', error);
      return false;
    }
  }, [favorites]);

  // Check if property is favorited
  const isFavorited = useCallback((propertyId: number): boolean => {
    return favorites.has(propertyId);
  }, [favorites]);

  // Clear all favorites (for logout)
  const clearFavorites = useCallback(() => {
    setFavorites(new Set());
  }, []);

  const value: FavoritesContextType = {
    favorites,
    isLoading,
    toggleFavorite,
    isFavorited,
    loadFavorites,
    clearFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

